import React, { Component, ReactNode } from 'react';
import { AppLogger, DatadogClient } from '@workivate/tho-web-shared';
import { ErrorPageContent } from './features/error-page/error-page-content/ErrorPageContent';
import { Page } from './layout/page/Page';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    AppLogger.logWithOptions({
      message: `ErrorBoundary caught an error: ${error.message}`,
      error: error,
    });

    // Send to DataDog
    const renderingError = new Error(error?.message);
    renderingError.name = 'ReactRenderingError';
    // Include component stack in the error message for DataDog
    (renderingError as any).componentStack = errorInfo.componentStack;
    (renderingError as any).originalError = error;

    DatadogClient.addError(renderingError);
    DatadogClient.logMessage(`FE USL Error: ${error?.message}`, {
      tags: ['error_boundary:react', 'component:error_boundary'],
    });
  }

  render() {
    if (this.state.hasError) {
      // If a custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Otherwise, use the default error page
      return (
        <Page>
          <ErrorPageContent />
        </Page>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for easier usage
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );
  WrappedComponent.displayName = `withErrorBoundary(${
    Component.displayName || Component.name || 'Component'
  })`;
  return WrappedComponent;
};
